import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useMultiLanguageWebsite } from "@/hooks/useMultiLanguageWebsite";
import VisualEditor from "@/components/VisualEditor";
import { 
  Save, 
  Eye, 
  LogOut, 
  FileText, 
  Settings, 
  Users, 
  Palette,
  Type,
  Languages,
  Monitor,
  Tablet,
  Smartphone,
  Plus,
  Edit,
  Trash2,
  Copy,
  Upload,
  Download,
  RefreshCw,
  Zap,
  Search,
  Globe,
  Image as ImageIcon,
  Video,
  Layout,
  Layers,
  MousePointer,
  Grid,
  Undo,
  Redo
} from "lucide-react";

interface ElementorAdminDashboardProps {
  onLogout: () => void;
}

const ElementorAdminDashboard = ({ onLogout }: ElementorAdminDashboardProps) => {
  const { toast } = useToast();
  const {
    website,
    currentLanguage,
    isLoading,
    switchLanguage,
    devicePreview,
    setDevicePreview,
    updatePage,
    updateTheme,
    getPage,
    getAllPages,
    getCurrentLanguageConfig,
    saveWebsite,
    undo,
    redo,
    canUndo,
    canRedo
  } = useMultiLanguageWebsite();

  const [activeTab, setActiveTab] = useState("pages");
  const [selectedPage, setSelectedPage] = useState("home");
  const [visualEditorOpen, setVisualEditorOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const languageConfig = getCurrentLanguageConfig();
  const currentWebsite = website.websites[currentLanguage];

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveWebsite(website);
      toast({
        title: currentLanguage === 'ar' ? "تم الحفظ بنجاح" : "Saved Successfully",
        description: currentLanguage === 'ar' ? "تم حفظ جميع التغييرات" : "All changes have been saved",
      });
    } catch (error) {
      toast({
        title: currentLanguage === 'ar' ? "خطأ في الحفظ" : "Save Error",
        description: currentLanguage === 'ar' ? "حدث خطأ أثناء الحفظ" : "An error occurred while saving",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuthenticated");
    localStorage.removeItem("adminLoginTime");
    toast({
      title: currentLanguage === 'ar' ? "تم تسجيل الخروج" : "Logged Out",
      description: currentLanguage === 'ar' ? "شكراً لاستخدام لوحة التحكم" : "Thank you for using the dashboard",
    });
    onLogout();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">
            {currentLanguage === 'ar' ? 'جاري التحميل...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  if (visualEditorOpen) {
    return (
      <VisualEditor
        pageId={selectedPage}
        onClose={() => setVisualEditorOpen(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background" dir={languageConfig.direction}>
      {/* Advanced Header */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-primary to-accent rounded-lg flex items-center justify-center">
                <Zap className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-bold">
                  {currentLanguage === 'ar' ? 'محرر الموقع المرئي' : 'Visual Website Builder'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {currentLanguage === 'ar' ? 'تحكم كامل بدون كود' : 'Complete No-Code Control'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Language Switcher */}
              <Select value={currentLanguage} onValueChange={(value: 'ar' | 'en') => switchLanguage(value)}>
                <SelectTrigger className="w-40">
                  <Languages className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(website.languages).map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      <div className="flex items-center space-x-2">
                        <span>{lang.flag}</span>
                        <span>{lang.nativeName}</span>
                        <Badge variant="outline" className="text-xs">
                          {lang.direction.toUpperCase()}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Device Preview */}
              <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
                <Button
                  variant={devicePreview === 'desktop' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setDevicePreview('desktop')}
                >
                  <Monitor className="h-4 w-4" />
                </Button>
                <Button
                  variant={devicePreview === 'tablet' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setDevicePreview('tablet')}
                >
                  <Tablet className="h-4 w-4" />
                </Button>
                <Button
                  variant={devicePreview === 'mobile' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setDevicePreview('mobile')}
                >
                  <Smartphone className="h-4 w-4" />
                </Button>
              </div>

              {/* History Controls */}
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={undo}
                  disabled={!canUndo}
                  title={currentLanguage === 'ar' ? 'تراجع' : 'Undo'}
                >
                  <Undo className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={redo}
                  disabled={!canRedo}
                  title={currentLanguage === 'ar' ? 'إعادة' : 'Redo'}
                >
                  <Redo className="h-4 w-4" />
                </Button>
              </div>

              <Button variant="outline" size="sm">
                <Eye className="h-4 w-4 mr-1" />
                {currentLanguage === 'ar' ? 'معاينة' : 'Preview'}
              </Button>
              
              <Button 
                onClick={handleSave} 
                disabled={isSaving}
                className="btn-glow"
              >
                <Save className="h-4 w-4 mr-1" />
                {isSaving 
                  ? (currentLanguage === 'ar' ? 'جاري الحفظ...' : 'Saving...') 
                  : (currentLanguage === 'ar' ? 'حفظ' : 'Save')
                }
              </Button>
              
              <Button variant="ghost" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-1" />
                {currentLanguage === 'ar' ? 'خروج' : 'Logout'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Advanced Sidebar */}
        <aside className="w-80 bg-card border-r border-border h-[calc(100vh-73px)] overflow-y-auto">
          <div className="p-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={currentLanguage === 'ar' ? 'البحث...' : 'Search...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} orientation="vertical">
              <TabsList className="grid w-full grid-cols-1 h-auto">
                <TabsTrigger value="pages" className="justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  {currentLanguage === 'ar' ? 'الصفحات' : 'Pages'}
                </TabsTrigger>
                <TabsTrigger value="design" className="justify-start">
                  <Palette className="h-4 w-4 mr-2" />
                  {currentLanguage === 'ar' ? 'التصميم' : 'Design'}
                </TabsTrigger>
                <TabsTrigger value="typography" className="justify-start">
                  <Type className="h-4 w-4 mr-2" />
                  {currentLanguage === 'ar' ? 'الخطوط' : 'Typography'}
                </TabsTrigger>
                <TabsTrigger value="media" className="justify-start">
                  <ImageIcon className="h-4 w-4 mr-2" />
                  {currentLanguage === 'ar' ? 'الوسائط' : 'Media'}
                </TabsTrigger>
                <TabsTrigger value="settings" className="justify-start">
                  <Settings className="h-4 w-4 mr-2" />
                  {currentLanguage === 'ar' ? 'الإعدادات' : 'Settings'}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 p-6 overflow-y-auto h-[calc(100vh-73px)]">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            
            {/* Pages Management */}
            <TabsContent value="pages" className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">
                  {currentLanguage === 'ar' ? 'إدارة الصفحات' : 'Page Management'}
                </h2>
                <div className="flex items-center space-x-2">
                  <Button>
                    <Plus className="h-4 w-4 mr-1" />
                    {currentLanguage === 'ar' ? 'صفحة جديدة' : 'New Page'}
                  </Button>
                  <Button
                    onClick={() => setVisualEditorOpen(true)}
                    className="btn-glow"
                  >
                    <MousePointer className="h-4 w-4 mr-1" />
                    {currentLanguage === 'ar' ? 'المحرر المرئي' : 'Visual Editor'}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {getAllPages().map((page) => (
                  <Card 
                    key={page.id} 
                    className={`cursor-pointer transition-all hover:shadow-lg ${
                      selectedPage === page.id ? 'ring-2 ring-primary' : ''
                    }`}
                    onClick={() => setSelectedPage(page.id)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{page.title}</CardTitle>
                        <Badge variant={page.status === 'published' ? 'default' : 'secondary'}>
                          {page.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{page.slug}</p>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {page.description}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline" className="text-xs">
                            {page.sections.length} {currentLanguage === 'ar' ? 'أقسام' : 'sections'}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            v{page.version}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPage(page.id);
                              setVisualEditorOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Copy page
                            }}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Delete page
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Page Details */}
              {selectedPage && getPage(selectedPage) && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {currentLanguage === 'ar' ? 'تفاصيل الصفحة' : 'Page Details'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>
                          {currentLanguage === 'ar' ? 'عنوان الصفحة' : 'Page Title'}
                        </Label>
                        <Input
                          value={getPage(selectedPage)?.title || ''}
                          onChange={(e) => {
                            updatePage(selectedPage, { title: e.target.value });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>
                          {currentLanguage === 'ar' ? 'رابط الصفحة' : 'Page Slug'}
                        </Label>
                        <Input
                          value={getPage(selectedPage)?.slug || ''}
                          onChange={(e) => {
                            updatePage(selectedPage, { slug: e.target.value });
                          }}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>
                        {currentLanguage === 'ar' ? 'وصف الصفحة' : 'Page Description'}
                      </Label>
                      <textarea
                        className="w-full h-20 p-3 border rounded-md resize-none"
                        value={getPage(selectedPage)?.description || ''}
                        onChange={(e) => {
                          updatePage(selectedPage, { description: e.target.value });
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Label>
                          {currentLanguage === 'ar' ? 'حالة النشر' : 'Status'}
                        </Label>
                        <Select
                          value={getPage(selectedPage)?.status || 'draft'}
                          onValueChange={(value: 'draft' | 'published' | 'scheduled') => {
                            updatePage(selectedPage, { status: value });
                          }}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">
                              {currentLanguage === 'ar' ? 'مسودة' : 'Draft'}
                            </SelectItem>
                            <SelectItem value="published">
                              {currentLanguage === 'ar' ? 'منشور' : 'Published'}
                            </SelectItem>
                            <SelectItem value="scheduled">
                              {currentLanguage === 'ar' ? 'مجدول' : 'Scheduled'}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Button
                        onClick={() => setVisualEditorOpen(true)}
                        className="btn-glow"
                      >
                        <Layout className="h-4 w-4 mr-2" />
                        {currentLanguage === 'ar' ? 'تحرير مرئي' : 'Visual Edit'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Design System */}
            <TabsContent value="design" className="space-y-6">
              <h2 className="text-2xl font-bold">
                {currentLanguage === 'ar' ? 'نظام التصميم' : 'Design System'}
              </h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {currentLanguage === 'ar' ? 'الألوان' : 'Colors'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(currentWebsite.theme.colors).map(([key, value]) => (
                        <div key={key} className="space-y-2">
                          <Label className="capitalize">{key}</Label>
                          <div className="flex items-center space-x-2">
                            <div
                              className="w-8 h-8 rounded border"
                              style={{ backgroundColor: value }}
                            />
                            <Input
                              type="color"
                              value={value}
                              onChange={(e) => {
                                updateTheme({
                                  colors: {
                                    ...currentWebsite.theme.colors,
                                    [key]: e.target.value
                                  }
                                });
                              }}
                              className="w-20"
                            />
                            <Input
                              value={value}
                              onChange={(e) => {
                                updateTheme({
                                  colors: {
                                    ...currentWebsite.theme.colors,
                                    [key]: e.target.value
                                  }
                                });
                              }}
                              className="flex-1"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>
                      {currentLanguage === 'ar' ? 'المسافات' : 'Spacing'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>
                          {currentLanguage === 'ar' ? 'وحدة المسافة' : 'Spacing Unit'}
                        </Label>
                        <div className="flex items-center space-x-2">
                          <Input
                            type="number"
                            value={currentWebsite.theme.spacing.unit}
                            onChange={(e) => {
                              updateTheme({
                                spacing: {
                                  ...currentWebsite.theme.spacing,
                                  unit: parseInt(e.target.value) || 4
                                }
                              });
                            }}
                            className="w-20"
                          />
                          <span className="text-sm text-muted-foreground">px</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>
                          {currentLanguage === 'ar' ? 'مقياس المسافات' : 'Spacing Scale'}
                        </Label>
                        <div className="grid grid-cols-4 gap-2">
                          {currentWebsite.theme.spacing.scale.map((value, index) => (
                            <div key={index} className="flex items-center space-x-1">
                              <Input
                                type="number"
                                value={value}
                                onChange={(e) => {
                                  const newScale = [...currentWebsite.theme.spacing.scale];
                                  newScale[index] = parseInt(e.target.value) || 0;
                                  updateTheme({
                                    spacing: {
                                      ...currentWebsite.theme.spacing,
                                      scale: newScale
                                    }
                                  });
                                }}
                                className="text-xs"
                              />
                              <div
                                className="w-4 h-4 bg-primary rounded"
                                style={{ 
                                  width: `${Math.min(value, 32)}px`,
                                  height: `${Math.min(value, 32)}px`
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Typography */}
            <TabsContent value="typography" className="space-y-6">
              <h2 className="text-2xl font-bold">
                {currentLanguage === 'ar' ? 'إدارة الخطوط' : 'Typography Management'}
              </h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {currentLanguage === 'ar' ? 'خطوط جوجل' : 'Google Fonts'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 gap-3">
                      {currentWebsite.theme.typography.fontFamilies.map((font) => (
                        <div key={font} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between">
                            <span style={{ fontFamily: font }} className="text-lg">
                              {font}
                            </span>
                            <Badge variant="outline">{font}</Badge>
                          </div>
                          <p style={{ fontFamily: font }} className="text-sm text-muted-foreground mt-2">
                            {currentLanguage === 'ar' 
                              ? 'نص تجريبي بالخط المحدد' 
                              : 'Sample text in selected font'
                            }
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>
                      {currentLanguage === 'ar' ? 'إعدادات الخطوط' : 'Font Settings'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Typography settings for different elements */}
                    {Object.entries(currentWebsite.theme.typography).map(([key, settings]) => {
                      if (key === 'fontFamilies') return null;
                      
                      return (
                        <div key={key} className="space-y-3">
                          <h4 className="font-medium capitalize">
                            {key === 'headings' ? (currentLanguage === 'ar' ? 'العناوين' : 'Headings') :
                             key === 'body' ? (currentLanguage === 'ar' ? 'النص الأساسي' : 'Body Text') :
                             key === 'buttons' ? (currentLanguage === 'ar' ? 'الأزرار' : 'Buttons') :
                             key === 'navigation' ? (currentLanguage === 'ar' ? 'التنقل' : 'Navigation') : key}
                          </h4>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">
                                {currentLanguage === 'ar' ? 'الخط' : 'Font'}
                              </Label>
                              <Select
                                value={settings.fontFamily}
                                onValueChange={(value) => {
                                  updateTheme({
                                    typography: {
                                      ...currentWebsite.theme.typography,
                                      [key]: {
                                        ...settings,
                                        fontFamily: value
                                      }
                                    }
                                  });
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {currentWebsite.theme.typography.fontFamilies.map((font) => (
                                    <SelectItem key={font} value={font}>
                                      {font}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="space-y-1">
                              <Label className="text-xs">
                                {currentLanguage === 'ar' ? 'الوزن' : 'Weight'}
                              </Label>
                              <Select
                                value={settings.fontWeight.toString()}
                                onValueChange={(value) => {
                                  updateTheme({
                                    typography: {
                                      ...currentWebsite.theme.typography,
                                      [key]: {
                                        ...settings,
                                        fontWeight: parseInt(value)
                                      }
                                    }
                                  });
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="300">Light (300)</SelectItem>
                                  <SelectItem value="400">Regular (400)</SelectItem>
                                  <SelectItem value="500">Medium (500)</SelectItem>
                                  <SelectItem value="600">Semi Bold (600)</SelectItem>
                                  <SelectItem value="700">Bold (700)</SelectItem>
                                  <SelectItem value="800">Extra Bold (800)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Media Library */}
            <TabsContent value="media" className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">
                  {currentLanguage === 'ar' ? 'مكتبة الوسائط' : 'Media Library'}
                </h2>
                <div className="flex items-center space-x-2">
                  <Button>
                    <Upload className="h-4 w-4 mr-1" />
                    {currentLanguage === 'ar' ? 'رفع ملف' : 'Upload'}
                  </Button>
                  <Button variant="outline">
                    <Download className="h-4 w-4 mr-1" />
                    {currentLanguage === 'ar' ? 'تصدير' : 'Export'}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {/* Placeholder for media items */}
                <div className="aspect-square bg-muted rounded-lg flex items-center justify-center border-2 border-dashed border-muted-foreground/25">
                  <div className="text-center">
                    <ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-xs text-muted-foreground">
                      {currentLanguage === 'ar' ? 'لا توجد صور' : 'No images'}
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Settings */}
            <TabsContent value="settings" className="space-y-6">
              <h2 className="text-2xl font-bold">
                {currentLanguage === 'ar' ? 'الإعدادات العامة' : 'General Settings'}
              </h2>
              
              <Card>
                <CardHeader>
                  <CardTitle>
                    {currentLanguage === 'ar' ? 'إعدادات الموقع' : 'Site Settings'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>
                        {currentLanguage === 'ar' ? 'اسم الموقع' : 'Site Name'}
                      </Label>
                      <Input
                        value={currentWebsite.settings.siteName}
                        onChange={(e) => {
                          // Update site settings
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>
                        {currentLanguage === 'ar' ? 'الشعار' : 'Tagline'}
                      </Label>
                      <Input
                        value={currentWebsite.settings.tagline}
                        onChange={(e) => {
                          // Update site settings
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>
                      {currentLanguage === 'ar' ? 'اللغة الافتراضية' : 'Default Language'}
                    </Label>
                    <Select value={website.defaultLanguage}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(website.languages).map((lang) => (
                          <SelectItem key={lang.code} value={lang.code}>
                            {lang.flag} {lang.nativeName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-4">
                    <Label>
                      {currentLanguage === 'ar' ? 'اللغات المفعلة' : 'Enabled Languages'}
                    </Label>
                    {Object.values(website.languages).map((lang) => (
                      <div key={lang.code} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{lang.flag}</span>
                          <div>
                            <p className="font-medium">{lang.nativeName}</p>
                            <p className="text-sm text-muted-foreground">
                              {lang.name} ({lang.direction.toUpperCase()})
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={lang.enabled}
                          onCheckedChange={(checked) => {
                            // Update language enabled state
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </main>
      </div>
    </div>
  );
};

export default ElementorAdminDashboard;